import { Fragment } from 'react'

import { AgentMessageComponent } from 'components/chat-components/AgentMessage'
import { ConversationComposer } from 'modules/coaching/components/ConversationComposer'
import styles from 'modules/coaching/components/VideoAgentScreen.module.css'

import type { UploadedVideoSource } from 'components/chat-components/AgentMessage.types'
import type { AgentMessage } from 'modules/coaching/types'
import type { FormEvent, KeyboardEvent, RefObject } from 'react'
import type { MuxPlaybackEmbed } from 'utils/videoUrlResolver'

type ConversationChatPanelBaseProperties = {
  agentError: string | undefined
  displayMessages: AgentMessage[]
  downloadFileName: string | undefined
  downloadVideoUrl: string | undefined
  initialUploadMessageIndex: number
  messagesEndReference: RefObject<HTMLDivElement | null>
  mux?: MuxPlaybackEmbed
  onPlaySegment: (startTime: string) => void
  onSeekToTime: (startTime: string) => void
  videoDuration: number | undefined
  videoReference: RefObject<HTMLVideoElement | null>
  videoSources: UploadedVideoSource[]
  videoUrl: string
}

type EditableConversationChatPanelProperties = ConversationChatPanelBaseProperties & {
  isReadOnly?: false
  attachedVideoCount: number
  inputPlaceholder: string
  inputValue: string
  isFollowUpUploadDisabled: boolean
  isInputDisabled: boolean
  isProcessing: boolean
  isRestoring?: boolean
  isStopping?: boolean
  isStopVisible?: boolean
  isUploadingFollowUpVideo: boolean
  followUpUploadError: string | undefined
  followUpUploadProgress: { percentage: number; phase: 'uploading' } | undefined
  onFollowUpVideoFilesDropped: (files: File[]) => void
  onInputChange: (value: string) => void
  onInputKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  onInputSubmit: (event?: FormEvent) => Promise<void>
  onInputTextArea: (event: FormEvent<HTMLTextAreaElement>) => void
  onStopResponse?: () => Promise<void>
}

type ReadOnlyConversationChatPanelProperties = ConversationChatPanelBaseProperties & {
  isReadOnly: true
}

type ConversationChatPanelProperties = EditableConversationChatPanelProperties | ReadOnlyConversationChatPanelProperties

const getProcessingStatusText = (properties: ConversationChatPanelProperties): string => {
  if (properties.isReadOnly) {
    return 'Analyzing...'
  }
  if (properties.isStopping) {
    return 'Stopping…'
  }
  return properties.isRestoring ? 'Restoring response…' : 'Analyzing...'
}

export const ConversationChatPanel = (properties: ConversationChatPanelProperties) => {
  /* --- Section 3: Derived Values --- */
  const {
    agentError,
    displayMessages,
    downloadFileName,
    downloadVideoUrl,
    initialUploadMessageIndex,
    messagesEndReference,
    mux,
    onPlaySegment,
    onSeekToTime,
    videoDuration,
    videoReference,
    videoSources,
    videoUrl,
  } = properties
  const processingStatusText = getProcessingStatusText(properties)

  /* --- Section 6: Render --- */
  return (
    <div className={styles.videoAgentContainer}>
      <div className={styles.videoAgentChatPanel}>
        <div className={styles.videoAgentMessagesContainer} data-testid="coach-messages-container">
          {displayMessages.map((message, messageIndex) => (
            <Fragment key={message.id}>
              <AgentMessageComponent
                downloadFileName={downloadFileName}
                downloadVideoUrl={downloadVideoUrl}
                isInitialUploadVideoMessage={messageIndex === initialUploadMessageIndex}
                message={message}
                mux={mux}
                onSeekToTime={onSeekToTime}
                onPlaySegment={onPlaySegment}
                videoReference={videoReference}
                videoUrl={videoUrl}
                videoDuration={videoDuration}
                videoSources={videoSources}
                isShowFunctionCalls={false}
              />
            </Fragment>
          ))}
          <div ref={messagesEndReference} />
        </div>

        {agentError ? <div className={styles.videoAgentErrorBanner}>{agentError}</div> : undefined}

        {properties.isReadOnly ? (
          <div className={styles.videoAgentReadOnlyNotice}>View-only conversation</div>
        ) : (
          <ConversationComposer
            cancelLabel="Stop response"
            cancelTestId="coach-stop-button"
            dataTestId="coach-chat-input"
            error={properties.followUpUploadError}
            inputLoadingContent={
              properties.isProcessing ? (
                <div className={styles.videoAgentInputLoading}>
                  <div className={styles.videoAgentInputLoadingDots}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className={styles.videoAgentInputLoadingText}>{processingStatusText}</span>
                </div>
              ) : undefined
            }
            isCancelDisabled={properties.isStopping}
            isCancelVisible={properties.isStopVisible}
            isInputDisabled={properties.isInputDisabled || properties.isProcessing}
            isSubmitDisabled={properties.isProcessing || !properties.inputValue.trim() || properties.isInputDisabled}
            isSubmitLoading={properties.isProcessing}
            onInput={properties.onInputTextArea}
            onInputChange={properties.onInputChange}
            onInputKeyDown={properties.onInputKeyDown}
            onCancel={properties.onStopResponse}
            onSubmit={properties.onInputSubmit}
            placeholder={properties.inputPlaceholder}
            progressPercentage={properties.followUpUploadProgress?.percentage}
            submitTestId="coach-send-button"
            uploadControl={{
              attachedVideoCount: properties.attachedVideoCount,
              isDisabled: properties.isFollowUpUploadDisabled,
              isUploading: properties.isUploadingFollowUpVideo,
              onFilesDropped: properties.onFollowUpVideoFilesDropped,
              testId: 'coach-follow-up-video-upload',
            }}
            value={properties.inputValue}
          />
        )}
      </div>
    </div>
  )
}
